import React, {
    cloneElement,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    Button,
    Flex,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    TextButton,
    Text,
} from '@zillow/constellation';

import { getDisplayPrice } from 'app/shared/utils/paidInclusionUtils';

import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';

import './style.scss';
import { StyledLeadGuaranteeBanner } from './StyledComponents';

const { PAYMENT_METHOD } = PaidInclusionConstants;

const defaultProps = {
    defaultCreditCard: () => {},
};

const SelectPaymentMethod = ({
    creditCardCapture,
    defaultCreditCard,
    defaultPaymentMethodSuccess,
    displaySelectPaymentMethodModal,
    // deprecated
    isEligibleForLeadGuarantee: isEligibleForLeadGuaranteeLegacy,
    isIframeLoaded,
    isRenderedInPageView,
    onPaymentMethodChange,
    onRefreshDefaultPaymentMethod,
    price,
    setCurrentPhase,
}) => {
    const creditCardCaptureRef = useRef(null);

    const { currentProperty } = useSelector(state => state.properties);

    const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethodSuccess
        ? PAYMENT_METHOD.DEFAULT
        : PAYMENT_METHOD.NEW);

    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        const shouldPerformScrollEffect = creditCardCaptureRef.current && isIframeLoaded
            && paymentMethod === PAYMENT_METHOD.NEW;
        if (shouldPerformScrollEffect) {
            // class is set in EditListingPage
            // dialog body is set via constellation ModalDialog component
            const modalBodyQuery = '[class^="DialogBody"]';

            // if SelectPaymentMethod is being rendered for mobile web view, scroll at the window level.
            const ref = isRenderedInPageView
                ? window
                : document.querySelector(modalBodyQuery);

            const creditCardCaptureOffsetTop = creditCardCaptureRef.current.offsetTop - 70;

            if (ref) {
                ref.scrollTo({
                    top: creditCardCaptureOffsetTop,
                    behavior: 'smooth',
                });
            }
        }
    }, [isIframeLoaded, paymentMethod, isRenderedInPageView]);

    const {
        paidInclusionSelectedProductType: selectedProductType,
        paidInclusion: {
            products,
        },
    } = currentProperty;

    // TODO: remove after we fully release feature
    const useLegacyData = products.length === 0;

    const foundIndex = products.findIndex(product => product.type === selectedProductType);
    const selectedProduct = products[foundIndex];
    const { isEligibleForLeadGuarantee } = selectedProduct;

    const legacyDisplayPrice = `${price} per week`;

    const displayPrice = useLegacyData
        ? legacyDisplayPrice
        : getDisplayPrice(selectedProduct);

    // TODO: remove after we fully release feature
    const isEligibleForLeadGuaranteeTemp = useLegacyData
        ? isEligibleForLeadGuaranteeLegacy
        : isEligibleForLeadGuarantee;

    const onRefreshDefaultPaymentMethodClick = () => {
        setShowSpinner(true);
        onRefreshDefaultPaymentMethod().then(() => {
            setShowSpinner(false);
        });
    };

    const { maskedNumber, cardBrand } = defaultCreditCard || {};
    const isDefaultPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.DEFAULT;
    const isNewPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.NEW;
    const renderPaymentMethods = () => (
        <React.Fragment>
            <Spacer
                paddingY="sm"
                className={cx('SpacerRow-radio', {
                    checked: isDefaultPaymentMethodSelected,
                })}
            >
                <LabeledControl
                    disabled={!defaultPaymentMethodSuccess}
                    label={(
                        <Label>
                            {
                                defaultPaymentMethodSuccess
                                    ? (
                                        <>
                                            {maskedNumber}
                                            <b className="card-brand">{cardBrand}</b>
                                        </>
                                    )
                                    : 'Default payment method'
                            }
                        </Label>
                    )}
                    control={(
                        <Radio
                            data-test="default-payment-method-radio"
                            name={PAYMENT_METHOD.DEFAULT}
                            onChange={() => {
                                setPaymentMethod(PAYMENT_METHOD.DEFAULT);
                                onPaymentMethodChange(PAYMENT_METHOD.DEFAULT);
                            }}
                            checked={isDefaultPaymentMethodSelected}
                        />
                    )}
                />
                {!defaultPaymentMethodSuccess ?
                    (
                        <Spacer paddingY="sm">
                            <Spacer paddingY="sm">
                                There was an error retrieving your default payment method.
                            </Spacer>
                            <TextButton onClick={() => onRefreshDefaultPaymentMethodClick()}>
                                Click here to refresh.
                            </TextButton>
                        </Spacer>
                    ) :
                    (<div />)
                }
                {showSpinner && (<SpinnerContainer size="md" />)}
            </Spacer>
            <Spacer
                paddingY="sm"
                className={cx('SpacerRow-radio', {
                    checked: isNewPaymentMethodSelected,
                })}
            >
                <LabeledControl
                    label={<Label>Replace with new card</Label>}
                    control={(
                        <Radio
                            data-test="new-payment-method-radio"
                            name={PAYMENT_METHOD.NEW}
                            onChange={() => {
                                setPaymentMethod(PAYMENT_METHOD.NEW);
                                onPaymentMethodChange(PAYMENT_METHOD.NEW);
                            }}
                            checked={isNewPaymentMethodSelected}
                        />
                    )}
                />
                {isNewPaymentMethodSelected &&
                    // select new payment method
                    // step: capture credit card
                    cloneElement(creditCardCapture, {
                        ref: creditCardCaptureRef,
                    })
                }
            </Spacer>
        </React.Fragment>
    );

    if (!displaySelectPaymentMethodModal) {
        return creditCardCapture;
    }

    return (
        <div className="SelectPaymentMethod">
            <Spacer paddingY="sm">
                <Text fontType="h5">Checkout</Text>
            </Spacer>
            <Spacer paddingY="sm">
                <Text fontType="body">
                    {selectedProductType === PaidInclusionConstants.PRODUCT_TYPE[1] && (
                        'Your listing will renew automatically until you deactivate it. '
                    )}
                    Your card will be charged
                    <strong type={selectedProductType} data-test="display-price">
                        {` ${displayPrice} `}
                    </strong>
                    while your listing is active on the Zillow Rental Network.
                </Text>

                {/* // todo: check against selected product.isEligibleForLeadGuarantee */}
                {isEligibleForLeadGuaranteeTemp && (
                    <StyledLeadGuaranteeBanner data-test="lead-guarantee-banner">
                        <Flex
                            display="flex"
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <img alt="" src={zGuaranteeIcon} />
                            <Text fontType="body">Includes Lead Guarantee</Text>
                        </Flex>
                    </StyledLeadGuaranteeBanner>
                )}
            </Spacer>
            <Spacer paddingY="sm">
                <Text fontType="h6">Payment method</Text>
            </Spacer>
            {renderPaymentMethods()}
            {isDefaultPaymentMethodSelected && (
                <div className="ModalActions">
                    <ListingActivationTerms actionText="By clicking Publish listing" />
                    <Spacer paddingY="sm">
                        <Button
                            data-test="checkout-button"
                            buttonType="primary"
                            fluid={true}
                            onClick={() => setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE)}
                        >
                            Publish listing
                        </Button>
                    </Spacer>
                </div>
            )}
        </div>
    );
};

SelectPaymentMethod.defaultProps = defaultProps;

SelectPaymentMethod.propTypes = {
    creditCardCapture: PropTypes.node.isRequired,
    defaultCreditCard: PropTypes.shape({
        cardBrand: PropTypes.string,
        defaultPaymentMethodExists: PropTypes.bool.isRequired,
        expirationMonth: PropTypes.string,
        expirationYear: PropTypes.string,
        maskedNumber: PropTypes.string,
    }),
    isIframeLoaded: PropTypes.bool.isRequired,
    onPaymentMethodChange: PropTypes.func.isRequired,
    onRefreshDefaultPaymentMethod: PropTypes.func.isRequired,
};

export default SelectPaymentMethod;
